import React from "react";
import {
  ArrowBack,
  ArrowDownward,
  ArrowForward,
  ArrowUpward,
  Close,
  VideocamOutlined,
  ZoomIn,
  ZoomOut,
} from "@mui/icons-material";
import { DEVICE_WEBSOCKET_STATE } from "../../features/deviceWebSocket/deviceWebSocketClient";
import { PTZ_MOVEMENTS } from "../../features/deviceWebSocket/deviceMessageProtocol";
import "./KioskPtzControls.css";

const getStatusPresentation = ({ connectionState, cameraStatus, pending, feedback }) => {
  if (connectionState !== DEVICE_WEBSOCKET_STATE.OPEN) {
    return { tone: "waiting", label: "Control link reconnecting" };
  }
  if (pending) return { tone: "working", label: feedback || "Applying camera movement" };
  if (feedback) return { tone: "error", label: feedback };
  if (!cameraStatus) return { tone: "waiting", label: "Checking kiosk camera" };
  if (cameraStatus.ptzReady) return { tone: "ready", label: "Camera control ready" };
  if (!cameraStatus.available) return { tone: "error", label: "OBSBOT camera not detected" };
  if (!cameraStatus.streaming) return { tone: "waiting", label: "Kiosk camera is off" };
  return { tone: "error", label: cameraStatus.message || "PTZ control unavailable" };
};

function KioskPtzControls({
  connectionState,
  cameraStatus,
  pending = false,
  feedback = "",
  onMove,
}) {
  const [controlsOpen, setControlsOpen] = React.useState(false);
  const capabilities = cameraStatus?.capabilities || { pan: false, tilt: false, zoom: false };
  const ready = connectionState === DEVICE_WEBSOCKET_STATE.OPEN
    && Boolean(cameraStatus?.ptzReady)
    && !pending;
  const statusPresentation = getStatusPresentation({
    connectionState,
    cameraStatus,
    pending,
    feedback,
  });

  if (!controlsOpen) {
    return (
      <button
        type="button"
        className="kiosk-ptz-launcher"
        onClick={() => setControlsOpen(true)}
        aria-expanded="false"
        aria-controls="kiosk-camera-controls"
        title={statusPresentation.label}
      >
        <VideocamOutlined aria-hidden="true" />
        <span className="kiosk-ptz-launcher__copy">
          <strong>Control camera</strong>
          <small aria-live="polite">{statusPresentation.label}</small>
        </span>
        <span
          className={`kiosk-ptz-launcher__status-dot kiosk-ptz-launcher__status-dot--${statusPresentation.tone}`}
          aria-hidden="true"
        />
      </button>
    );
  }

  const movementButton = ({ movement, label, Icon, capability }) => (
    <button
      type="button"
      className="kiosk-ptz-button"
      onClick={() => onMove(movement)}
      disabled={!ready || !capabilities[capability]}
      aria-label={label}
      title={label}
    >
      <Icon />
    </button>
  );

  return (
    <aside id="kiosk-camera-controls" className="kiosk-ptz-panel" aria-label="Kiosk camera controls">
      <header className="kiosk-ptz-panel__header">
        <div className="kiosk-ptz-panel__identity">
          <strong>Kiosk camera</strong>
          <span>{cameraStatus?.cameraLabel || "OBSBOT Tiny 2"}</span>
        </div>
        <div className="kiosk-ptz-panel__header-actions">
          <VideocamOutlined className="kiosk-ptz-panel__camera-icon" aria-hidden="true" />
          <button
            type="button"
            className="kiosk-ptz-panel__close"
            onClick={() => setControlsOpen(false)}
            aria-label="Close camera controls"
            title="Close camera controls"
          >
            <Close />
          </button>
        </div>
      </header>

      <div
        className={`kiosk-ptz-panel__status kiosk-ptz-panel__status--${statusPresentation.tone}`}
        role="status"
        aria-live="polite"
      >
        <span className="kiosk-ptz-panel__status-dot" aria-hidden="true" />
        <span>{statusPresentation.label}</span>
      </div>

      <div className="kiosk-ptz-pad" aria-label="Pan and tilt controls">
        <span />
        {movementButton({
          movement: PTZ_MOVEMENTS.UP,
          label: "Tilt camera up",
          Icon: ArrowUpward,
          capability: "tilt",
        })}
        <span />
        {movementButton({
          movement: PTZ_MOVEMENTS.LEFT,
          label: "Pan camera left",
          Icon: ArrowBack,
          capability: "pan",
        })}
        <span className="kiosk-ptz-pad__center" aria-hidden="true"><span /></span>
        {movementButton({
          movement: PTZ_MOVEMENTS.RIGHT,
          label: "Pan camera right",
          Icon: ArrowForward,
          capability: "pan",
        })}
        <span />
        {movementButton({
          movement: PTZ_MOVEMENTS.DOWN,
          label: "Tilt camera down",
          Icon: ArrowDownward,
          capability: "tilt",
        })}
        <span />
      </div>

      <div className="kiosk-ptz-zoom" aria-label="Zoom controls">
        {movementButton({
          movement: PTZ_MOVEMENTS.ZOOM_OUT,
          label: "Zoom camera out",
          Icon: ZoomOut,
          capability: "zoom",
        })}
        <span>Zoom</span>
        {movementButton({
          movement: PTZ_MOVEMENTS.ZOOM_IN,
          label: "Zoom camera in",
          Icon: ZoomIn,
          capability: "zoom",
        })}
      </div>
    </aside>
  );
}

export default KioskPtzControls;
